import React from 'react'
import { Helmet } from 'react-helmet'
import { Button, Container, Icon, Spacer, Text, Form, Modal, Toggle, Loader } from 'roadie-ui'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import Panel, { PanelTitle, PanelSpacer } from 'components/layout/Panel'
import { getVehicleCategoryIcon, VehicleCard, VehicleCardProps } from '../Vehicles'
import { getVehicleDisplayName, preventSpecialCharacters, VehicleFormStates } from 'util/vehicle'
import { ModalOptions } from 'types/global'
import { FeatureFlag } from 'Optimizely'
import { Redirect } from 'react-router-dom'
import { useInsurancePollingContext } from 'context/InsurancePollingContext'
import { states } from 'types/states'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import {
  BumperServerError,
  LicenseSearchFailure,
  SearchingForVehicle,
  UnsupportedTypeError,
  VehicleAddedConfirmation,
} from '../NewVehicle/NewVehiclePage'
import SectionMessage from 'components/layout/SectionMessage'
import { InsuranceInput } from './InsuranceSubmission'
import { useVehicleInfoViewModel } from './hooks/useVehicleInfoPageViewModel'
import { useVehicleInfoPageViewModel } from './hooks/useVehicleInfoPageViewModel'
import { useLicensePlateOnlyInputViewModel } from './hooks/useVehicleInfoPageViewModel'
import { PendingPlateLookupModal } from '../PendingPlateLookupModal'
import { useLocalization, LocalizedText } from 'localization'

const FieldActionButton: React.FC<{
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
  color?: 'primary-base' | 'neutral-base'
}> = ({ onClick, disabled, children, color = 'primary-base' }) => (
  <Button.Tertiary type="button" onClick={onClick} disabled={disabled}>
    <Text.Body1 $fontWeight="500" $color={disabled ? 'neutral-base' : color}>
      {children}
    </Text.Body1>
  </Button.Tertiary>
)

const FieldEditActions: React.FC<{
  onSave: () => void
  onCancel: () => void
  saveDisabled?: boolean
  saveColor?: 'primary-base' | 'neutral-base'
}> = ({ onSave, onCancel, saveDisabled, saveColor = 'primary-base' }) => (
  <Container.Grid $gridTemplateColumns="max-content max-content" $gridGap="0.8rem" $justifyContent="end">
    <FieldActionButton onClick={onSave} disabled={saveDisabled} color={saveColor}>
      SAVE
    </FieldActionButton>
    <FieldActionButton onClick={onCancel}>CANCEL</FieldActionButton>
  </Container.Grid>
)

const ReadOnlyEditableField: React.FC<{
  label: string
  value?: string | null
  onEdit: () => void
  disabled?: boolean
}> = ({ label, value, onEdit, disabled }) => (
  <Container.Grid $gridTemplateColumns="1fr max-content" $gridGap="0.8rem" $alignItems="end">
    <Container.Position
      $position="relative"
      $minWidth="0"
      $borderStyle="none none solid"
      $borderWidth="1px"
      $borderColor="neutral-light"
    >
      <Container.Position $position="absolute" $top="1rem" $left="0.4rem">
        <Text.Caption $color="neutral-base" $fontWeight="500">
          {label}
        </Text.Caption>
      </Container.Position>
      <Container.Base $padding="2.5rem 0.4rem 1rem">
        <Text.Body1 $color="neutral-light">{value}</Text.Body1>
      </Container.Base>
    </Container.Position>
    <Container.GridItem
      $alignSelf="end"
      $padding="0 0 0.8rem"
      $borderStyle="none none solid"
      $borderWidth="1px"
      $borderColor="neutral-light"
    >
      <FieldActionButton onClick={onEdit} disabled={disabled}>
        EDIT
      </FieldActionButton>
    </Container.GridItem>
  </Container.Grid>
)

const LicensePlateOnlyInput: React.FC<{
  licensePlate?: string | null
  licensePlateState?: string | null
  vehicleId: number
  disabled?: boolean
  licensePlateLookupFailure?: boolean | false
  missingVIN: boolean
  setFormState: React.Dispatch<React.SetStateAction<VehicleFormStates | null>>
  setLicensePlate: React.Dispatch<React.SetStateAction<any>>
  setLicensePlateState?: React.Dispatch<React.SetStateAction<string | null>>
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
}> = (props) => {
  const { t } = useLocalization()
  const {
    licensePlateInputValue,
    hasChanges,
    isEditingPlate,
    isEditingState,
    displayErrorLabel,
    handleInputChange,
    handleLicensePlateStateChange,
    beginEdit,
    cancelEdit,
    handleSubmit,
  } = useLicensePlateOnlyInputViewModel({
    licensePlate: props.licensePlate,
    licensePlateState: props.licensePlateState,
    vehicleId: props.vehicleId,
    disabled: props.disabled,
    setLicensePlateState: props.setLicensePlateState,
    missingVIN: props.missingVIN,
    setFormState: props.setFormState,
    setLicensePlate: props.setLicensePlate,
    setModalOptions: props.setModalOptions,
  })
  const saveDisabled = props.disabled || !(licensePlateInputValue && props.licensePlateState) || !hasChanges
  const saveColor = saveDisabled ? 'neutral-base' : 'primary-base'

  return (
    <>
      <Container.Grid $padding="1rem 2.0rem 2.0rem 2.0rem">
        <Form.Basic validations={[['license_plate', Form.validations.required, 'License plate is required']]}>
          <Container.Grid
            $gridTemplateColumns="minmax(0, 3fr) minmax(30rem, 1fr)"
            $gridTemplateColumnsMedia={{ mobile: '1fr', tablet: '1fr' }}
            $gridGap="1rem"
            $gridGapMedia={{ mobile: '1.6rem', tablet: '1.6rem' }}
            $alignItems="start"
          >
            <Container.Base>
              {isEditingPlate ? (
                <Container.Grid
                  $gridTemplateColumns="1fr max-content"
                  $gridTemplateColumnsMedia={{ mobile: '1fr' }}
                  $gridGap="0.8rem"
                  $alignItems="end"
                >
                  <Form.Input
                    label={t('dweb_account_driving_info_vehicle_info_license_plate')}
                    type="text"
                    name="license_plate"
                    maxLength={8}
                    value={licensePlateInputValue}
                    disabled={props.disabled}
                    onChange={handleInputChange}
                    onKeyDown={preventSpecialCharacters}
                  />
                  <Container.GridItem
                    $alignSelf="end"
                    $justifySelfMedia={{ mobile: 'end' }}
                    $padding="0 0 0.8rem"
                    $borderStyle="none none solid"
                    $borderWidth="1px"
                    $borderColor="neutral-light"
                  >
                    <FieldEditActions
                      onSave={handleSubmit}
                      onCancel={() => cancelEdit('plate')}
                      saveDisabled={saveDisabled}
                      saveColor={saveColor}
                    />
                  </Container.GridItem>
                </Container.Grid>
              ) : (
                <ReadOnlyEditableField
                  label="License Plate"
                  value={licensePlateInputValue}
                  onEdit={() => beginEdit('plate')}
                  disabled={props.disabled}
                />
              )}
              {props.licensePlateLookupFailure && props.missingVIN && (
                <>
                  <Spacer $height="0.4rem" />
                  <Text.Caption $color="error-base">
                    <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_license_plate_not_found" />
                  </Text.Caption>
                </>
              )}
            </Container.Base>
            <Container.Base>
              <Container.Position $position="relative">
                {displayErrorLabel ? (
                  <Container.Position $position="absolute" $left="0.5rem" $top="1rem">
                    <label>
                      <Text.Caption $textAlign="left" $color="error-base">
                        <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_state" />
                      </Text.Caption>
                    </label>
                  </Container.Position>
                ) : null}
                {isEditingState ? (
                  <Container.Grid
                    $gridTemplateColumns="1fr max-content"
                    $gridTemplateColumnsMedia={{ mobile: '1fr' }}
                    $gridGap="0.8rem"
                    $alignItems="end"
                  >
                    <Form.Select
                      key="state"
                      label={displayErrorLabel ? 'Select' : 'State'}
                      name="state"
                      value={props.licensePlateState || ''}
                      disabled={props.disabled}
                      onChange={handleLicensePlateStateChange}
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </Form.Select>
                    <Container.GridItem
                      $alignSelf="end"
                      $justifySelfMedia={{ mobile: 'end' }}
                      $padding="0 0 0.8rem"
                      $borderStyle="none none solid"
                      $borderWidth="1px"
                      $borderColor="neutral-light"
                    >
                      <FieldEditActions
                        onSave={handleSubmit}
                        onCancel={() => cancelEdit('state')}
                        saveDisabled={saveDisabled}
                        saveColor={saveColor}
                      />
                    </Container.GridItem>
                  </Container.Grid>
                ) : (
                  <ReadOnlyEditableField
                    label="State"
                    value={props.licensePlateState}
                    onEdit={() => beginEdit('state')}
                    disabled={props.disabled}
                  />
                )}
              </Container.Position>
            </Container.Base>
          </Container.Grid>
        </Form.Basic>
      </Container.Grid>
    </>
  )
}

const VehicleInfoModal: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  isWebView: boolean
}> = ({ modalOptions, setModalOptions, isWebView }) => {
  return (
    <Modal.Basic
      open={modalOptions?.modalOpen && modalOptions?.modalType == 'vehicle-info'}
      width={isWebView ? '350px' : '500px'}
    >
      <Container.Base $maxHeight="90vh">
        <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
          <Button.Tertiary type="button" onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}>
            <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
          </Button.Tertiary>
        </Container.Position>
        <Spacer $height="1.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info" />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Body1>
          <LocalizedText translationKey="dweb_vehicle_info_unlock_more_info" />
        </Text.Body1>
        <Spacer $height="2.4rem" />
        <Button.Secondary
          $borderWidth="0"
          $width="100%"
          type="button"
          onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}
        >
          <LocalizedText translationKey="dweb_common_close" />
        </Button.Secondary>
      </Container.Base>
    </Modal.Basic>
  )
}

const DeleteVehicleModal: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  isWebView: boolean
  handleDelete: () => void
}> = ({ modalOptions, setModalOptions, isWebView, handleDelete }) => {
  const { t } = useLocalization()
  return (
    <Modal.Basic
      open={modalOptions?.modalOpen && modalOptions?.modalType == 'delete-vehicle'}
      title={t('dweb_account_driving_info_vehicle_info_sure_want_delete_vehicle')}
      width={isWebView ? '350px' : '500px'}
    >
      <Container.Base $maxHeight="90vh">
        <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
          <Button.Tertiary type="button" onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}>
            <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
          </Button.Tertiary>
        </Container.Position>
        <Spacer $height="1.4rem" />
        <Text.Body1>
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_by_deleting_will_no_longer_able_use_vehicle" />
        </Text.Body1>
        <Spacer $height="2.4rem" />
        <Button.Primary $borderWidth="0" $width="100%" type="button" onClick={handleDelete}>
          <LocalizedText translationKey="dweb_common_delete" />
        </Button.Primary>
        <Spacer $height="1.4rem" />
        <Button.Secondary
          $borderWidth="0"
          $width="100%"
          type="button"
          onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}
        >
          <LocalizedText translationKey="dweb_common_cancel" />
        </Button.Secondary>
      </Container.Base>
    </Modal.Basic>
  )
}

const ConflictingVINModal: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  isWebView: boolean
  vinConflicts: Array<{ displayName: string; typeId: number | null; vin: string }>
  handleConflictingVIN: (data: any) => Promise<void>
}> = ({ modalOptions, setModalOptions, isWebView, vinConflicts, handleConflictingVIN }) => {
  return (
    <Modal.Basic
      open={modalOptions?.modalOpen && modalOptions?.modalType == 'conflicting-vin-found'}
      width={isWebView ? '350px' : '500px'}
    >
      <Container.Base $maxHeight="90vh">
        <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
          <Button.Tertiary type="button" onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}>
            <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
          </Button.Tertiary>
        </Container.Position>
        <Spacer $height="1.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_which_vehicle_yours" />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Body1>
          <LocalizedText translationKey="dweb_vehicle_info_two_matches_prompt" />
        </Text.Body1>
        <Spacer $height="2.4rem" />

        <Form.Basic id="conflicting-vin-modal-form" onSubmit={handleConflictingVIN}>
          <Container.Grid $justifyContent="center">
            <Form.Radio.Group name="conflicting-vin-select">
              {vinConflicts?.map((conflict, index) => (
                <React.Fragment key={conflict.typeId}>
                  <Form.Radio.Field value={String(index)}>
                    <Text.Body1 $color="neutral-black" $fontSize="medium">
                      {conflict.displayName}
                    </Text.Body1>
                  </Form.Radio.Field>
                  <Spacer $height="1.4rem" />
                </React.Fragment>
              ))}
            </Form.Radio.Group>
          </Container.Grid>
          <Spacer $height="2.4rem" />
          <Form.Submit>
            <LocalizedText translationKey="dweb_common_save" />
          </Form.Submit>
        </Form.Basic>

        <Spacer $height="1.4rem" />
        <Button.Secondary
          $borderWidth="0"
          $width="100%"
          type="button"
          onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}
        >
          <LocalizedText translationKey="dweb_common_close" />
        </Button.Secondary>
      </Container.Base>
    </Modal.Basic>
  )
}

const UploadInProgressModal: React.FC<{
  modalOptions?: ModalOptions
  setModalOptions?: React.Dispatch<React.SetStateAction<ModalOptions>>
  isWebView: boolean
}> = ({ modalOptions, setModalOptions, isWebView }) => {
  return (
    <Modal.Basic
      open={modalOptions?.modalOpen && modalOptions?.modalType == 'insurance-upload-in-progress'}
      width={isWebView ? '350px' : '500px'}
    >
      <Container.Base $maxHeight="90vh">
        <Container.Position $position="absolute" $top="1.4rem" $right="1.4rem">
          <Button.Tertiary type="button" onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}>
            <Icon.Close $width="2rem" $height="2rem" $color="neutral-base" />
          </Button.Tertiary>
        </Container.Position>
        <Spacer $height="1.4rem" />
        <Loader size="60px" color="primary-base" />
        <Spacer $height="2.4rem" />
        <Text.H4 $fontWeight="700" $color="neutral-black">
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_upload_progress" />
        </Text.H4>
        <Spacer $height="2.4rem" />
        <Text.Body1>
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_may_take_few_minutes_may_leave_page_check" />
        </Text.Body1>
        <Spacer $height="2.4rem" />

        <Button.Secondary
          $borderWidth="0"
          $width="100%"
          type="button"
          onClick={() => setModalOptions?.({ modalOpen: false, modalType: null })}
        >
          <LocalizedText translationKey="dweb_common_close" />
        </Button.Secondary>
      </Container.Base>
    </Modal.Basic>
  )
}

const VehicleInfo: React.FC<VehicleCardProps> = ({
  icon,
  title,
  active,
  vehicle,
  allVehicles,
  modalOptions,
  setModalOptions,
}) => {
  const { t } = useLocalization()
  const { status } = useInsurancePollingContext()

  const {
    isWebView,
    isLocked,
    isActive,
    licensePlateState,
    setLicensePlateState,
    formState,
    setFormState,
    licensePlate,
    setLicensePlate,
    onRoute,
    licensePlateLookupFailure,
    missingLicensePlate,
    missingVIN,
    errorLabel,
    vinConflicts,
    driverHasActiveBlock,
    driverActiveBlockStatus,
    handleActiveVehicleToggle,
    handleDelete,
    handleConflictingVIN,
  } = useVehicleInfoViewModel(vehicle, active, setModalOptions)

  // Loading screen while vehicles is looking up license plate
  if (formState == 'searchingVehicle')
    return (
      <Panel>
        <SearchingForVehicle />
      </Panel>
    )

  // Confirmation of new vehicle added
  if (formState == 'vehicleFound')
    return (
      <Panel>
        <VehicleAddedConfirmation
          vehicle={vehicle}
          vehicleId={vehicle.details.id}
          redirectPath={`${vehicle.details.id}`}
          setFormState={setFormState}
        />
      </Panel>
    )

  // Bumper server error
  if (formState === 'serverError') {
    return (
      <Panel>
        <BumperServerError
          handleClose={() => {
            setFormState(null)
          }}
        />
      </Panel>
    )
  }

  // Unsupported vehicle type error
  if (formState == 'unsupportedType') {
    return (
      <Panel>
        <UnsupportedTypeError vehicle_id={vehicle.details.id} newVehicleFlow={false} setFormState={setFormState} />
      </Panel>
    )
  }

  // License plate search failed
  if (formState == 'vehicleNotFound')
    return (
      <Panel>
        <LicenseSearchFailure
          vehicle_id={vehicle.details.id}
          newVehicleFlow={false}
          license_plate={licensePlate}
          setFormState={setFormState}
          modalOptions={modalOptions}
          setModalOptions={setModalOptions}
        />
      </Panel>
    )

  // Default state of the page
  return (
    <>
      <Panel noPadding>
        <VehicleCard
          icon={icon}
          title={title}
          active={active}
          vehicle={vehicle}
          page="vehicle-info"
          modalOptions={modalOptions}
          setModalOptions={setModalOptions}
          errorLabel={errorLabel}
        />
      </Panel>
      <PanelSpacer />
      {(missingLicensePlate || missingVIN) && (
        <FeatureFlag feature="drive_enforce_license_plate_and_vin_requirement">
          {(enabled) => {
            return (
              enabled && (
                <SectionMessage description={t('dweb_vehicle_info_missing_info_active_blocked')} iconType="warning" />
              )
            )
          }}
        </FeatureFlag>
      )}
      <Spacer $height="2.4rem" />
      <Panel left noPadding>
        <Container.Grid $padding="2.0rem" $gridTemplateColumns="1fr max-content" $alignItems="center">
          <Container.GridItem>
            <Text.Subtitle1 $fontWeight="medium" $color="neutral-black">
              <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_active_vehicle" />
            </Text.Subtitle1>
            <Spacer $height="0.7rem" />
            <Text.Caption>
              {onRoute
                ? t('dweb_account_driving_info_vehicle_info_active_vehicle_on_route_description')
                : driverActiveBlockStatus === 'loading'
                  ? t('dweb_account_driving_info_vehicle_info_active_vehicle_block_loading_description')
                  : driverHasActiveBlock
                    ? t('dweb_account_driving_info_vehicle_info_active_vehicle_on_block_description')
                    : active
                      ? t('dweb_account_driving_info_vehicle_info_active_vehicle_description')
                      : t('dweb_account_driving_info_vehicle_info_inactive_vehicle_description')}
            </Text.Caption>
          </Container.GridItem>
          <Container.GridItem $padding="0 0 0 2rem">
            <Container.Base $height="2rem" $width="3rem">
              <FeatureFlag feature="drive_enforce_license_plate_and_vin_requirement">
                {(enabled) => {
                  return (
                    <Toggle
                      value={isActive}
                      onChange={
                        isLocked ||
                        onRoute ||
                        driverActiveBlockStatus !== 'loaded' ||
                        driverHasActiveBlock ||
                        ((missingLicensePlate || missingVIN) && enabled)
                          ? () => Promise.resolve()
                          : handleActiveVehicleToggle
                      }
                    />
                  )
                }}
              </FeatureFlag>
            </Container.Base>
          </Container.GridItem>
        </Container.Grid>
      </Panel>
      <PanelSpacer />
      <Container.Grid $gridTemplateColumns="max-content 1fr" $alignItems="center" $gridGap="1rem">
        <PanelTitle>
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info" />
        </PanelTitle>
        {(missingLicensePlate || (licensePlateLookupFailure && missingVIN)) && (
          <FeatureFlag feature="drive_enforce_license_plate_and_vin_requirement">
            {(enabled, variables) => {
              const color = variables?.alert_color == 2 ? 'error-base' : 'alert-base'
              return enabled && <Icon.AlertFilled $color={color} $width="1.6rem" $height="1.6rem" />
            }}
          </FeatureFlag>
        )}
      </Container.Grid>
      <Panel left noPadding>
        <LicensePlateOnlyInput
          vehicleId={vehicle.details.id}
          licensePlate={vehicle.details.license_plate}
          licensePlateState={licensePlateState}
          setLicensePlateState={setLicensePlateState}
          disabled={isLocked}
          licensePlateLookupFailure={licensePlateLookupFailure}
          missingVIN={missingVIN}
          setFormState={setFormState}
          setLicensePlate={setLicensePlate}
          modalOptions={modalOptions}
          setModalOptions={setModalOptions}
        />
      </Panel>
      <PanelSpacer />
      <FeatureFlag feature="drive_allow_insurance_uploads">
        {(enabled: boolean) => {
          return (
            enabled && (
              <>
                <PanelTitle>
                  <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_documents" />
                </PanelTitle>
                <Panel left noPadding>
                  <InsuranceInput
                    modalOptions={modalOptions}
                    setModalOptions={setModalOptions}
                    vehicle_id={vehicle.details.id}
                    expiration_date={vehicle.insurance.expiration_date}
                    status={vehicle.insurance.status}
                    disabled={isLocked}
                  />
                </Panel>
                <PanelSpacer />
              </>
            )
          )
        }}
      </FeatureFlag>
      {allVehicles?.length == 1 ? (
        <>
          <Spacer $height="1rem" />
          <Container.Base $textAlign="center" $padding="0 1rem">
            <Text.Caption>
              <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_must_have_at_least_one_vehicle_profile_add" />
            </Text.Caption>
          </Container.Base>
        </>
      ) : (
        active && (
          <>
            <Spacer $height="1rem" />
            <Container.Base $textAlign="center" $padding="0 1rem">
              <Text.Caption>
                <LocalizedText translationKey="dweb_vehicle_info_active_delete_blocked" />
              </Text.Caption>
            </Container.Base>
          </>
        )
      )}

      <Container.Base
        $paddingMedia={{ ultrawide: '2rem 10rem', desktop: '2rem 10rem', tablet: '2rem 10rem', mobile: '2rem' }}
        $textAlign="center"
      >
        <Button.Secondary
          $color="error-base"
          disabled={active || allVehicles?.length == 1 || status == 'polling' || isLocked}
          $width="100%"
          $borderColor="error-base"
          type="button"
          onClick={() => setModalOptions?.({ modalOpen: true, modalType: 'delete-vehicle' })}
        >
          <LocalizedText translationKey="dweb_account_driving_info_vehicle_info_delete_vehicle" />
        </Button.Secondary>
      </Container.Base>

      {/* Modals which are hidden by default */}
      <VehicleInfoModal modalOptions={modalOptions} setModalOptions={setModalOptions} isWebView={isWebView} />
      <DeleteVehicleModal
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
        isWebView={isWebView}
        handleDelete={handleDelete}
      />
      <ConflictingVINModal
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
        isWebView={isWebView}
        vinConflicts={vinConflicts}
        handleConflictingVIN={handleConflictingVIN}
      />
      <UploadInProgressModal modalOptions={modalOptions} setModalOptions={setModalOptions} isWebView={isWebView} />
    </>
  )
}

type VehicleInfoPageProps = {
  match: {
    params: {
      vehicle_id: string
    }
  }
}

const VehicleInfoPage: React.FC<VehicleInfoPageProps> = (props) => {
  const { t } = useLocalization()
  const vehicle_id = Number(props.match.params.vehicle_id)
  const { isWebView, vehicles, activeVehicleId, modalOptions, setModalOptions, vehicle } =
    useVehicleInfoPageViewModel(vehicle_id)

  if (!vehicle) {
    return <Redirect push to={{ pathname: '/account/driving-info' }} />
  }

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_account_driving_info_vehicle_info')}</title>
      </Helmet>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_driving_info'), linkTo: '/account/driving-info' },
            { label: t('dweb_account_driving_info_vehicle_info') },
          ]}
        />
      )}
      <VehicleInfo
        key={vehicle.details.id}
        icon={getVehicleCategoryIcon(vehicle.specs.category, vehicle.specs.model, vehicle.details.vin)}
        title={getVehicleDisplayName(vehicle)}
        active={vehicle.details.id == activeVehicleId}
        vehicle={vehicle}
        allVehicles={vehicles}
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
      />
      <PendingPlateLookupModal vehicle={vehicle} modalOptions={modalOptions} setModalOptions={setModalOptions} />
    </Page>
  )
}

export default VehicleInfoPage
